/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PhoneInput from '../shared/PhoneInput';
import { isValidInternationalPhone } from '../../utils/phoneUtils';
import '../../styles/Auth.css';
import AuthBrandHeader from './AuthBrandHeader';
import { useBranding } from '../../contexts/BrandingContext';
import OtpVerification from './OtpVerification';

const SignUp = () => {
  const { branding } = useBranding();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    unity_id: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    contact_number: '',
    address: '',
    is_ncsu: false
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [projectsFound, setProjectsFound] = useState(false);
  const [projectCount, setProjectCount] = useState(0);
  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // No longer need to fetch signup data since we removed the dependent fields
    setLoading(false);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear the institution ID when the user unticks the institution option
    if (name === 'is_ncsu' && !checked) {
      setFormData(prevState => ({
        ...prevState,
        unity_id: ''
      }));
    }
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.email) return 'Email is required';
        if (!emailRegex.test(formData.email)) return 'Please enter a valid email';
        if (formData.is_ncsu && !formData.unity_id) return 'Institution ID is required for institution members';
        break;
      case 2:
        if (!formData.password) return 'Password is required';
        if (formData.password.length < 6) return 'Password must be at least 6 characters';
        if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
        if (!formData.first_name || !formData.last_name) return 'Name fields are required';
        break;
      case 3:
        if (!formData.contact_number) return 'Contact number is required';
        if (!isValidInternationalPhone(formData.contact_number)) return 'Please enter a valid phone number';
        if (!formData.address) return 'Address is required';
        // institution role is assigned by admins after signup
        break;
    }
    return null;
  };

  const handleNextStep = () => {
    const error = validateStep();
    if (error) {
      setError(error);
      return;
    }
    setStep(step + 1);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Only proceed with submission if we're on the last step
    if (step !== 3) {
      return;
    }

    try {
      const response = await axios.post('/api/auth/signup', formData);

      if (response.data.requiresVerification) {
        setAwaitingOtp(true);
        return;
      }

      // Check if there were projects associated with this email
      // Handle both new user registration (201) and completing existing user registration (200)
      if (response.data.projectsFound && response.data.projectCount > 0) {
        setProjectsFound(true);
        setProjectCount(response.data.projectCount);
        setSignupSuccess(true);
      } else {
        // No projects found, show success and redirect to signin
        setSignupSuccess(true);
        setTimeout(() => {
          navigate('/signin');
        }, 1500);
      }
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.response?.data?.message || error.response?.data?.error || 'An error occurred during signup');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNextStep();
    }
  };

  const renderStep1 = () => (
    <>
      <div className="checkbox-container">
        <input
          type="checkbox"
          name="is_ncsu"
          checked={formData.is_ncsu}
          onChange={handleChange}
        />
        <label>Are you associated with the institution?</label>
      </div>
      <div className="form-group">
        <label>Email:</label>
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          required
        />
      </div>
      {formData.is_ncsu && (
        <div className="form-group">
          <label>Unity ID:</label>
          <input
            type="text"
            name="unity_id"
            placeholder="Unity ID"
            value={formData.unity_id}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            required
          />
        </div>
      )}
      <button type="button" className="auth-button" onClick={handleNextStep}>Next</button>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="form-group">
        <label>First Name:</label>
        <input
          type="text"
          name="first_name"
          placeholder="First Name"
          value={formData.first_name}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          required
        />
      </div>
      <div className="form-group">
        <label>Last Name:</label>
        <input
          type="text"
          name="last_name"
          placeholder="Last Name"
          value={formData.last_name}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          required
        />
      </div>
      <div className="form-group">
        <label>Password:</label>
        <input
          type="password"
          name="password"
          placeholder="Password"
          value={formData.password}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          required
        />
      </div>
      <div className="form-group">
        <label>Confirm Password:</label>
        <input
          type="password"
          name="confirmPassword"
          placeholder="Confirm Password"
          value={formData.confirmPassword}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          required
        />
      </div>
      <div className="button-group">
        <button type="button" className="auth-button" onClick={() => setStep(1)}>Back</button>
        <button type="button" className="auth-button" onClick={handleNextStep}>Next</button>
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <div className="form-group">
        <label>Contact Number:</label>
        <PhoneInput
          name="contact_number"
          value={formData.contact_number}
          onChange={(v) => setFormData(prev => ({ ...prev, contact_number: v }))}
          required
        />
      </div>
      <div className="form-group">
        <label>Address:</label>
        <input
          type="text"
          name="address"
          placeholder="Address"
          value={formData.address}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          required
        />
      </div>
      <div className="button-group">
        <button type="button" className="auth-button" onClick={() => setStep(2)}>Back</button>
        <button type="submit" className="auth-button">Sign Up</button>
      </div>
    </>
  );

  // Success component
  const renderSuccessMessage = () => (
    <div className="success-message">
      <h2>Registration Successful!</h2>
      <p>Your account has been {projectsFound ? 'completed' : 'created'} successfully.</p>
      
      {projectsFound && (
        <div className="projects-found-message">
          <p>🎉 Great news! We found {projectCount} {projectCount === 1 ? 'project' : 'projects'} where you were previously added as a team member.</p>
          <p>These projects have been linked to your account, and you can access them immediately after signing in.</p>
        </div>
      )}
      
      <button 
        onClick={() => navigate('/signin')}
        className="auth-button"
      >
        Sign In
      </button>
    </div>
  );

  return (
    <div className="auth-page">
      <AuthBrandHeader
        heading={branding.signup_heading || 'Plant Controlled Environment Laboratory Services Login'}
        subheading={branding.signup_subheading}
      />

      <div className="auth-container">
        {awaitingOtp ? (
          <OtpVerification
            email={formData.email}
            onVerified={() => navigate('/')}
            onBack={() => setAwaitingOtp(false)}
          />
        ) : signupSuccess ? (
          renderSuccessMessage()
        ) : (
          <>
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit} className="auth-form">
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
            </form>
            
            <div className="auth-link">
              <p>Already have an account? <Link to="/signin">Sign In</Link></p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SignUp;